import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Select,
  message,
  Row,
  Col,
  Card,
  Tag,
  Pagination
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, HistoryOutlined } from '@ant-design/icons';
import { SidebarLayout } from '../components/SidebarLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Icons } from '../components/Icons';

const { Option } = Select;

const api = `${process.env.REACT_APP_API_URL}/devices`;

const DeviceForm = ({
  visible,
  onCancel,
  onSubmit,
  initialValues,
  types,
  status
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) {
      form.setFieldsValue(initialValues || {});
    } else {
      form.resetFields();
    }
  }, [visible, initialValues]);

  return (
    <Modal
      visible={visible}
      title={initialValues ? 'Editar Dispositivo' : 'Novo Dispositivo'}
      onCancel={onCancel}
      onOk={() => {
        form.validateFields()
          .then(values => {
            form.resetFields();
            onSubmit(values);
          });
      }}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="dsdeveui"
          label="DevEUI"
          rules={[
            { required: true, message: 'DevEUI é obrigatório' },
            { max: 12, message: 'Máximo de 12 caracteres' },
            { pattern: /^[A-Za-z0-9]+$/, message: 'Apenas letras e números' }
          ]}
        >
          <Input maxLength={12} />
        </Form.Item>
        <Form.Item
          name="dstag"
          label="Tag (opcional)"
          rules={[
            { max: 14, message: 'Máximo de 14 caracteres' },
            { pattern: /^[A-Za-z0-9-]*$/, message: 'Apenas letras, números e hífen' }
          ]}
        >
          <Input maxLength={14} />
        </Form.Item>
        <Form.Item
          name="cdtype"
          label="Tipo"
          rules={[]}
        >
          <Select
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
            allowClear
          >
            {types.map(type => (
              <Option key={type.id} value={type.id}>
                {type.dsname}
              </Option>
            ))}
          </Select>
        </Form.Item>        <Form.Item
          name="cddisplaystatus"
          label="Status"
          rules={[]}
        >
          <Select
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
            allowClear
          >
            {status.map(s => (
              <Option key={s.id} value={s.id}>
                {s.dsname}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const DeviceHistoryModal = ({ visible, onCancel, history, isControlPanel }) => {
  function formatDate(isoString) {
    if (!isoString || isoString === null) return 'Atual';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;
    const pad = n => n.toString().padStart(2, '0');
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  const columns = [
    { title: 'Evento', dataIndex: 'description', key: 'description' },
    { title: 'Data', dataIndex: 'timestamp', key: 'timestamp', render: formatDate }
  ];

  let dataSource = history || [];

  if (isControlPanel) {
    if (history && history.id) {
      dataSource = [{
        id: history.id,
        description: `Dispositivo vinculado ao cavalo ${history.dsplate}`,
        timestamp: 'Atual'
      }];
    } else {
      dataSource = [];
    }
  } else if (Array.isArray(dataSource)) {
    const atualItems = dataSource.filter(item => item.timestamp === null);
    const otherItems = dataSource.filter(item => item.timestamp !== null)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    dataSource = [...atualItems, ...otherItems];
  }

  return (
    <Modal
      visible={visible}
      title="Histórico do Dispositivo"
      onCancel={onCancel}
      footer={[
        <Button key="close" onClick={onCancel}>Fechar</Button>
      ]}
      width={800}
    >
      <Table
        dataSource={dataSource}
        columns={columns}
        rowKey="id"
        locale={{ emptyText: isControlPanel ? 'Nenhum cavalo vinculado a este dispositivo.' : 'Nenhum histórico.' }}
        pagination={false}
      />
    </Modal>
  );
};

const ApiKeyModal = ({ visible, apiKey, onClose }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Modal
      visible={visible}
      title="Chave de API"
      onCancel={onClose}
      footer={null}
      width={480}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Input value={apiKey} readOnly style={{ flex: 1 }} />
        <Button onClick={handleCopy} type="primary">
          {copied ? 'Copiado!' : 'Copiar'}
        </Button>
      </div>
    </Modal>
  );
};

export const Devices = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [visible, setVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [selectedDeviceHistory, setSelectedDeviceHistory] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [messageApi, contextHolder] = message.useMessage();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;
  const [types, setTypes] = useState([]);
  const [status, setStatus] = useState([]);
  const [apiKeyModalVisible, setApiKeyModalVisible] = useState(false);
  const [apiKeyToShow, setApiKeyToShow] = useState('');
  const [typeFilter, setTypeFilter] = useState(null);
  const [isControlPanelHistory, setIsControlPanelHistory] = useState(false);

  const { user, selectedClient } = useAuth();

  useEffect(() => {
    fetchData();
    fetchTypes();
    fetchStatus();
  }, [selectedClient]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const url = `${api}?selectedClient=${selectedClient.client.id}`
      const res = await axios.get(url);
      setData(res.data);
    } catch {
      messageApi.error('Erro ao buscar dispositivos');
    }
    setLoading(false);
  };

  const fetchTypes = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/device_type`);
      setTypes(res.data);
    } catch {
      messageApi.error('Erro ao buscar tipos de dispositivo');
    }
  };
  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/display_status`);
      setStatus(res.data);
    } catch {
      messageApi.error('Erro ao buscar status de dispositivo');
    }
  };

  const handleCreate = async values => {
    try {
      values.idcustomer = selectedClient.client.id;
      await axios.post(api, values);
      messageApi.success('Dispositivo criado com sucesso');
      setVisible(false);
      fetchData();
    } catch (error) {
      var apiMsg = 'Erro ao criar dispositivo';
      if (error.response?.status != 500) {
        apiMsg +=  ': ' + error.response?.data?.error || '!';
      }
      messageApi.error(apiMsg);
    }
  };

  const handleUpdate = async values => {
    try {
      await axios.put(`${api}/${editing.id}`, values);
      messageApi.success('Dispositivo atualizado com sucesso');
      setVisible(false);
      setEditing(null);
      fetchData();
    } catch (error) {
      var apiMsg = 'Erro ao atualizar dispositivo';
      if (error.response?.status != 500) {
        apiMsg +=  ': ' + error.response?.data?.error || '!';
      }
      messageApi.error(apiMsg);
    }
  };

  const handleDelete = id => {
    Modal.confirm({
      title: 'Tem certeza que deseja remover este dispositivo?',
      content: 'Esta ação não poderá ser desfeita, todas as mensagens e eventos associados a este dispositivo serão perdidos.',
      okText: 'Sim, remover',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await axios.delete(`${api}/${id}`);
          messageApi.success('Dispositivo removido com sucesso');
          fetchData();
        } catch (error) {
          var apiMsg = 'Erro ao remover dispositivo';
          if (error.response?.status != 500) {
            apiMsg +=  ': ' + error.response?.data?.error || '!';
          }
          messageApi.error(apiMsg);
        }
      }
    });
  };

  const handleViewHistory = async (device) => {
    if (device.cdtype == process.env.REACT_APP_CONTROL_PANEL_TYPE_ID) {
      setIsControlPanelHistory(true);
      setHistoryModalVisible(true);
      setSelectedDeviceHistory({});
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/truck_horses/find_by_device`, {
          params: { iddevice: device.id }
        });
        setSelectedDeviceHistory(res.data);
      } catch (err) {
        setSelectedDeviceHistory({});
      }
      return;
    }
    setIsControlPanelHistory(false);
    try {
      const res = await axios.get(`${api}/${device.id}/history`);
      setSelectedDeviceHistory(res.data);
      setHistoryModalVisible(true);
    } catch {
      messageApi.error('Erro ao buscar histórico do dispositivo');
    }
  };

  const columns = [
    { title: 'DevEUI', dataIndex: 'dsdeveui', key: 'dsdeveui' },
    {
      title: 'Tipo',
      dataIndex: 'cdtype',
      render: code => types.find(t => t.id === code)?.dsname || code
    },    {
      title: 'Status',
      dataIndex: 'cddisplaystatus',
      render: code => status.find(s => s.id === code)?.dsname || code
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<HistoryOutlined />}
            onClick={() => handleViewHistory(record)}
          />
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setEditing(record);
              setVisible(true);
            }}
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      )
    }
  ];

  return (
    <SidebarLayout>
      {contextHolder}
      <div style={{ padding: 24 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
            flexWrap: 'wrap',
            gap: 12
          }}
        >
          <h1 style={{ fontSize: 30}}>
            Dispositivos
          </h1>
          <div
            style={{
              minWidth: 200,
              width: '100%',
              maxWidth: 320,
              marginTop: 8,
              marginLeft: 'auto',
              ...(window.innerWidth <= 600
                ? { order: 2, marginTop: 12, marginLeft: 0 }
                : { order: 0, marginTop: 0 })
            }}
          >
            <Select
              allowClear
              placeholder="Filtrar por tipo"
              style={{ width: '100%' }}
              value={typeFilter}
              onChange={setTypeFilter}
            >
              {types.map(type => (
                <Option key={type.id} value={type.id}>{type.dsname}</Option>
              ))}
            </Select>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              setVisible(true);
            }}
          >
            Novo
          </Button>
          <Input.Search
            placeholder="Buscar"
            allowClear
            onSearch={value => setSearchText(value)}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 180 }}
          />
        </div>

        <Row gutter={[42, 32]}>
          {data
            .filter(item =>
              item.dsdeveui?.toLowerCase().includes(searchText.toLowerCase()) &&
              (typeFilter ? item.cdtype === typeFilter : true)
            )
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map(device => (
              <Col xs={24} sm={12} md={8} lg={8} key={device.id}>
              <Card
                actions={user.isAdmin && device.cdtype == process.env.REACT_APP_CONTROL_PANEL_TYPE_ID ?
                  [
                  <HistoryOutlined key="history" onClick={() => handleViewHistory(device)} />,
                  <EditOutlined key="edit" onClick={() => { setEditing(device); setVisible(true); }} />,
                  <DeleteOutlined key="delete" style={{ color: 'red' }} onClick={() => handleDelete(device.id)} />,
                  <Button type="transparent"
                    onClick={async () => {
                      try {
                        let resp = await axios.get(`${api}/${device.id}/api_key`);
                        let api_key = resp.data.api_key;
                        if (!api_key) {
                          messageApi.error('Chave de API não encontrada');
                          return;
                        }
                        setApiKeyToShow(api_key);
                        setApiKeyModalVisible(true);
                      } catch (err) {
                        messageApi.error('Erro ao buscar chave de API');
                      }
                    }}
                  ><i className='fa fa-key'/></Button>
                  ]
                  : [
                  <HistoryOutlined key="history" onClick={() => handleViewHistory(device)} />,
                  <EditOutlined key="edit" onClick={() => { setEditing(device); setVisible(true); }} />,
                  <DeleteOutlined key="delete" style={{ color: 'red' }} onClick={() => handleDelete(device.id)} />
                  ]
                }
                className='fade-in'
                style={{
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  borderRadius: 16,
                  padding: 0,
                  border: '1px solid #f0f0f0',
                  minHeight: 180,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
                bodyStyle={{
                  padding: 24,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                  <img src={Icons.deviceBlack} alt="device" style={{ width: 30,  marginRight: 12, marginLeft: -6 }} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 18, color: '#222' }}>{device.dsdeveui}</div>
                    <div style={{ color: '#888', fontSize: 13 }}>
                      {types.find(t => t.id === device.cdtype)?.dsname || device.cdtype}
                    </div>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', rowGap: 8 }}>
                    <div style={{ display: 'contents' }}>
                      <span style={{ fontWeight: 500 }}>
                        <i className='fa fa-id-card' style={{marginRight: 4}}></i> DevEUI:
                      </span>
                      <span style={{ color: '#333' }}>{device.dsdeveui}</span>
                    </div>
                    <div style={{ display: 'contents' }}>
                      <span style={{ fontWeight: 500 }}>
                        <i className='fa fa-tag' style={{marginRight: 7}}></i> Tipo:
                      </span>
                      <span style={{ color: '#333' }}>{types.find(t => t.id === device.cdtype)?.dsname || device.cdtype}</span>
                    </div>
                    <div style={{ display: 'contents' }}>
                      <span style={{ fontWeight: 500 }}>
                        <i className='fa fa-check-circle' style={{marginRight: 7}}></i> Status:
                      </span>
                      <span style={{ color: '#333' }}>{status.find(s => s.id === device.cddisplaystatus)?.dsname || device.cddisplaystatus}</span>
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'end' }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={data.filter(item =>
              item.dsdeveui?.toLowerCase().includes(searchText.toLowerCase()) &&
              (typeFilter ? item.cdtype === typeFilter : true)
            ).length}
            onChange={page => setCurrentPage(page)}
            showSizeChanger={false}
          />
        </div>

        <DeviceForm
          visible={visible}
          onCancel={() => {
            setVisible(false);
            setEditing(null);
          }}
          onSubmit={editing ? handleUpdate : handleCreate}
          initialValues={editing}
          types={types}
          status={status}
        />

        <DeviceHistoryModal
          visible={historyModalVisible}
          onCancel={() => setHistoryModalVisible(false)}
          history={selectedDeviceHistory}
          isControlPanel={isControlPanelHistory}
        />

        <ApiKeyModal
          visible={apiKeyModalVisible}
          apiKey={apiKeyToShow}
          onClose={() => setApiKeyModalVisible(false)}
        />
      </div>
    </SidebarLayout>
  );
};
